import { Injectable } from '@angular/core';
import { Case } from '../../shared/models/case';
import { Observable } from 'rxjs';
import { DRResponse} from '../../shared/models/response';
import { DrFileMeta, DrFileMetaCategoryList, DrFileMetaList, DrFileMetaOpData } from '../../shared/models/dr-file';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { HttpHeaders } from '@angular/common/http';
import { BaseHttpWithBundleService } from '@app/core/services/base/base-service-with-bundle.service';
import { HttpServiceBundle } from '@app/core/services/base/http-service-bundle.service';
import { FileUtilityService } from '@app/core/services/file-utility.service';
import { ClientUser } from '@app/shared/models/client-user';
import { ClientMediaType, MediaContainer } from '@app/shared/models/media-container';
import { BackendUriUnits } from '@app/core/services/base/backend-uri-units';
import { BaseWithId } from '@app/shared/models/base';
import { Office } from '@app/shared/models/office';
import { Role } from '@app/core/authentication/permission';
import { ClaimType } from '@app/shared/models/claim-type';

@Injectable({
  providedIn: 'root'
})
export class ContentStoreService extends BaseHttpWithBundleService {

  private readonly metaPostfix = '/metadata';

  get caseUri() {
    return `${this.uri}/${BackendUriUnits.CASES}/`;
  }

  get userUri() {
    return `${environment.apiUrl}/${BackendUriUnits.API}/${BackendUriUnits.CONTENT_STORE}/${BackendUriUnits.FILES}/${BackendUriUnits.USERS}/`;
  }

  get officeUri() {
    return `${environment.apiUrl}/${BackendUriUnits.API}/${BackendUriUnits.CONTENT_STORE}/${BackendUriUnits.FILES}/${BackendUriUnits.OFFICES}/`;
  }

  get workInstructionUri() {
    return `${environment.apiUrl}/${BackendUriUnits.API}/${BackendUriUnits.CONTENT_STORE}/${BackendUriUnits.CLAIM_TYPES}/`;
  }

  constructor(public httpServiceBundle: HttpServiceBundle,
              protected fileUtilityService: FileUtilityService) {
    super(httpServiceBundle);
  }


  // CASE CONTENT STORE ************************************************************************************************

  public addCaseFile(caseX: Case | string, fileAsBinaryString: string, fileMeta: DrFileMeta): Observable<DrFileMeta> {
    const uri = this.constructCaseFileUri(caseX);
    const queryParams = fileMeta.toQueryParams();
    return this.addMediaFile(uri, fileAsBinaryString, queryParams, true);
  }

  public getCaseFile(caseX: Case | string, fileMeta: DrFileMeta | string): Observable<any> {
    const uri = this.constructCaseFileUri(caseX, fileMeta, true);
    return this.http.get<DRResponse<any>>(uri);
  }

  public deleteCaseFile(caseX: Case | string, fileMeta: DrFileMeta | string): Observable<any> {
    const uri = this.constructCaseFileUri(caseX, fileMeta);
    return this.deleteMediaFile(uri);
  }

  public editCaseFile(caseX: Case | string, fileAsBinaryString: string, fileMeta: DrFileMeta): Observable<DrFileMeta> {
    const uri = this.constructCaseFileUri(caseX, fileMeta);
    return this.saveMediaFile(uri, fileAsBinaryString, null, true);
  }

  public getCaseFileMetaList(caseX: Case | string, categories?: string[], role?: Role): Observable<DrFileMetaCategoryList> {
    const uri = this.constructCaseFileUri(caseX) + this.metaPostfix;

    const queryParams: any = {};

    if (categories && categories.length > 0) {
      queryParams.document_types = categories.join();
    }

    if (role) {
      queryParams.role = role;
    }

    return this.http.get<DRResponse<DrFileMetaCategoryList>>(uri, { params: queryParams })
                    .pipe(map(res => new DrFileMetaCategoryList(res.data)));
  }

  public getCaseFileMeta(caseX: Case | string, fileId: string): Observable<DrFileMeta> {
    const uri = this.constructCaseFileUri(caseX, fileId, false) + this.metaPostfix;
    return this.http.get<DRResponse<DrFileMeta>>(uri)
                    .pipe(map(res => new DrFileMeta(res.data)));
  }

  public updateCaseFileMeta(caseX: Case | string, fileMeta: DrFileMeta | string, metaData: any): Observable<any> {
    const uri = this.constructCaseFileUri(caseX, fileMeta, false) + this.metaPostfix;
    return this.http.put<DRResponse<any>>(uri, metaData);
  }

  public reorderCaseFileMeta(caseX: Case | string, reorderPatches: DrFileMetaOpData[]): Observable<any> {
    const uri = this.constructCaseFileUri(caseX);
    const patches = reorderPatches.map(drFileMetaOpData => {
      return {
        op: drFileMetaOpData.op,
        path: drFileMetaOpData.path,
        value: drFileMetaOpData.value
      };
    });
    return this.http.patch<DRResponse<DrFileMetaCategoryList>>(uri, patches);
  }

  protected constructCaseFileUri(caseX: Case | string, fileMeta?: DrFileMeta | string, withToken?: boolean): string {
    const caseId = caseX instanceof Case ? caseX.id : caseX;
    const fileId = fileMeta ? (typeof fileMeta === 'string' ? fileMeta : fileMeta.id) : null;
    let uri = `${this.caseUri}${caseId}/${BackendUriUnits.ATTACHMENTS}${ fileId ? `/${fileId}` : `` }`;
    uri += withToken ? this.fileUtilityService.addToken() : '';
    return uri;
  }

  // WORK INSTRUCTIONS **************************************************************************************************

  public addWorkInstructionFile(claimType: ClaimType | string, fileAsBinaryString: string, fileMeta: DrFileMeta): Observable<DrFileMeta> {
    const uri = this.constructWorkInstructionFileUri(this.workInstructionUri, claimType);
    const queryParams = fileMeta.toQueryParams();
    delete queryParams.smp_category; // We do not want so send smp_category to the BE as for the moment this is FE only internal use
    return this.addMediaFile(uri, fileAsBinaryString, queryParams, false)
               .pipe(map((res: any) => {
                 res.properties = res.properties || {};
                 res.properties.smp_category = res.properties.smp_category || (typeof claimType === 'string' ? claimType : claimType.id);
                 return res;
               }));
  }

  public deleteWorkInstructionFile(claimType: ClaimType | string, fileMeta: DrFileMeta | string): Observable<any> {
    const uri = this.constructWorkInstructionFileUri(this.workInstructionUri, claimType, fileMeta);
    return this.deleteMediaFile(uri);
  }

  public reorderWorkInstructionMeta(claimType: ClaimType | string, reorderPatches: DrFileMetaOpData[]): Observable<any> {
    const uri = this.constructWorkInstructionFileUri(this.workInstructionUri, claimType);
    const patches = reorderPatches.map(drFileMetaOpData => {
      return {
        op: drFileMetaOpData.op,
        path: drFileMetaOpData.path,
        value: drFileMetaOpData.value
      };
    });
    return this.http.patch<DRResponse<DrFileMetaList>>(uri, patches);
  }

  public getWorkInstructionFileMetaList(claimType: ClaimType): Observable<DrFileMetaList> {
    const uri = this.constructWorkInstructionFileUri(this.workInstructionUri, claimType);
    return this.http.get<DRResponse<DrFileMeta[]>>(uri)
                    .pipe(map((res: any) => {
          res.forEach((fileMeta: any) => {
            fileMeta.properties = fileMeta.properties || {};
            fileMeta.properties.smp_category = fileMeta.properties.smp_category || claimType.id;
          });

          res = {
            document_type: claimType.id,
            entries: res
          };

          return new DrFileMetaList(res);
        }
      ));
  }

  protected constructWorkInstructionFileUri(uriPrefix: string, claimType: ClaimType | string, fileMeta?: DrFileMeta | string, withToken?: boolean): string {
    const claimTypeId = claimType ? (typeof claimType === 'string' ? claimType : claimType.id) : null;
    const fileId = fileMeta ? (typeof fileMeta === 'string' ? fileMeta : fileMeta.id) : null;
    let uri = `${uriPrefix}${claimTypeId}/${BackendUriUnits.WORK_INSTRUCTIONS}${fileId ? `/${fileId}` : ''}`;
    uri += withToken ? this.fileUtilityService.addToken() : '';
    return uri;
  }


  // USER CONTENT STORE ************************************************************************************************

  public getUserMediaFiles(user: ClientUser | string): Observable<MediaContainer> {
    const uri = this.constructMediaFileUri(this.userUri, user);
    return this.getMediaFiles(uri);
  }

  public addUserMediaFile(user: ClientUser | string, type: ClientMediaType, fileAsBinaryString: string, fileMeta: DrFileMeta): Observable<DrFileMeta> {
    const uri = this.constructMediaFileUri(this.userUri, user, type, fileMeta);
    const queryParams = fileMeta.toQueryParams();
    return this.addMediaFile(uri, fileAsBinaryString, queryParams);
  }

  public deleteUserMediaFile(user: ClientUser | string, type: ClientMediaType, fileMeta: DrFileMeta | string): Observable<any> {
    const uri = this.constructMediaFileUri(this.userUri, user, type, fileMeta);
    return this.deleteMediaFile(uri);
  }


  // OFFICE CONTENT STORE ************************************************************************************************

  public getOfficeMediaFiles(office: Office | string): Observable<MediaContainer> {
    const uri = this.constructMediaFileUri(this.officeUri, office);
    return this.getMediaFiles(uri);
  }

  public addOfficeMediaFile(office: Office | string, type: ClientMediaType, fileAsBinaryString: string, fileMeta: DrFileMeta): Observable<DrFileMeta> {
    const uri = this.constructMediaFileUri(this.officeUri, office, type, fileMeta);
    const queryParams = fileMeta.toQueryParams();
    return this.addMediaFile(uri, fileAsBinaryString, queryParams);
  }

  public deleteOfficeMediaFile(office: Office  | string, type: ClientMediaType, fileMeta: DrFileMeta | string): Observable<any> {
    const uri = this.constructMediaFileUri(this.officeUri, office, type, fileMeta);
    return this.deleteMediaFile(uri);
  }

  // VARIA *************************************************************************************************************

  public getMediaFiles(uri: string): Observable<MediaContainer> {
    return this.http.get<DRResponse<MediaContainer>>(uri)
                    .pipe(map(res => new MediaContainer(res)));
  }

  public addMediaFile(uri: string, fileAsBinaryString: string, queryParams: any, responseWithDataProp?: boolean): Observable<DrFileMeta> {
    const options: any = { headers: new HttpHeaders().set('Content-Type', 'application/octet-stream') };

    if (queryParams) {
      options.params = queryParams;
    }

    return this.http.post<DRResponse<DrFileMeta>>(uri, fileAsBinaryString, options)
                    .pipe(map((res: any) => new DrFileMeta(responseWithDataProp ? res.data : res)));
  }

  public saveMediaFile(uri: string, fileAsBinaryString: string, queryParams: any, responseWithDataProp?: boolean): Observable<DrFileMeta> {
    const options: any = { headers: new HttpHeaders().set('Content-Type', 'application/octet-stream') };

    if (queryParams) {
      options.params = queryParams;
    }

    return this.http.put<DRResponse<DrFileMeta>>(uri, fileAsBinaryString, options)
                    .pipe(map((res: any) => new DrFileMeta(responseWithDataProp ? res.data : res)));
  }

  public deleteMediaFile(uri: string): Observable<any> {
    return this.http.delete<DRResponse<MediaContainer>>(uri)
                    .pipe(res => res);
  }

  protected constructMediaFileUri(uriPrefix: string, baseWithId: BaseWithId | string, type?: ClientMediaType, fileMeta?: DrFileMeta | string, withToken?: boolean): string {
    const id = baseWithId instanceof BaseWithId ? baseWithId.id : baseWithId;
    const mediaType = type ? type : null;
    const fileId = fileMeta ? (typeof fileMeta === 'string' ? fileMeta : fileMeta.id) : null;

    let uri = `${uriPrefix}${id}/media${mediaType ? `/${mediaType}${ fileId ? `/${fileId}` : `` }` : ``}`;

    uri += withToken ? this.fileUtilityService.addToken() : '';
    return uri;
  }
}
